import type { IncidentDecisionState, IncidentNotificationState, IncidentOwnerEscalationState, IncidentSignalSource, IncidentStatus } from "./incidents.js";
import type {
  CredentialStatus,
  SecretAccessDecision,
  SecretAccessReasonCode,
  ToolActionReasonCode,
  ToolExecutionDecision,
  ToolRunbookReadinessFailureCode
} from "./tooling.js";
import { SharedPolicyClassValue, SharedWorkflowStateValue } from "./sharedValues.js";

export enum PolicyClass {
  SafeRead = SharedPolicyClassValue.SafeRead,
  BoundedWrite = "bounded_write",
  Destructive = "destructive",
  CredentialAccess = "credential_access",
  ExternalWrite = "external_write",
  Deploy = "deploy",
  GitPublication = "git_publication"
}

export const policyClasses = [
  PolicyClass.SafeRead,
  PolicyClass.BoundedWrite,
  PolicyClass.Destructive,
  PolicyClass.CredentialAccess,
  PolicyClass.ExternalWrite,
  PolicyClass.Deploy,
  PolicyClass.GitPublication
] as const;

const approvalRequired = new Set<PolicyClass>([
  PolicyClass.Destructive,
  PolicyClass.CredentialAccess,
  PolicyClass.ExternalWrite,
  PolicyClass.Deploy,
  PolicyClass.GitPublication
]);

export function isPolicyClass(value: string | null | undefined): value is PolicyClass {
  return value !== null && value !== undefined && policyClasses.some((policyClass) => policyClass === value);
}

export function policyRequiresApproval(policyClass: PolicyClass): boolean {
  return approvalRequired.has(policyClass);
}

export enum ApprovalState {
  NotRequired = "not_required",
  Required = "required",
  Pending = "pending",
  Approved = SharedWorkflowStateValue.Approved,
  Denied = "denied",
  Expired = "expired"
}

export enum AuditAction {
  IncidentSignalReceived = "incident.signal.received",
  IncidentGithubSynced = "incident.github.synced",
  IncidentGithubSyncFailed = "incident.github.sync_failed",
  IncidentResolved = "incident.resolved",
  IncidentLlmFailed = "incident.llm.failed",
  IncidentNotificationApprovalRequired = "incident.notification.approval_required",
  IncidentHumanDecisionQueued = "incident.human_decision.queued",
  IncidentOwnerEscalationQueued = "incident.owner_escalation.queued",
  IncidentRemediationSessionRequested = "incident.remediation_session.requested",
  ToolRunbookListedForStoredIncident = "tool.runbook.listed_for_stored_incident",
  ToolRunbookMcpActionsListed = "tool.runbook.mcp_actions_listed",
  ToolRunbookListedForIncident = "tool.runbook.listed_for_incident",
  ToolRunbookPreviewed = "tool.runbook.previewed",
  ToolRunbookRegistered = "tool.runbook.registered",
  ToolInfrastructureReadinessInspected = "tool.infrastructure.readiness_inspected",
  ToolSecretReferenceImported = "tool.secret_reference.imported",
  ToolCredentialGrantCreated = "tool.credential_grant.created",
  ToolSecretAccessRequested = "tool.secret_access.requested",
  ToolExecutionRequested = "tool.execution.requested",
  ToolLlmPromptRedacted = "tool.llm_prompt.redacted"
}

export enum AuditResourceType {
  Incident = "incident",
  IncidentNotification = "incident_notification",
  IncidentDecision = "incident_decision",
  IncidentOwnerEscalation = "incident_owner_escalation",
  IncidentRunbookRegistry = "incident_runbook_registry",
  ToolRunbook = "tool_runbook",
  ToolInfrastructureReadiness = "tool_infrastructure_readiness",
  SecretReference = "secret_reference",
  ToolCredentialGrant = "tool_credential_grant",
  SecretAccessRequest = "secret_access_request",
  ToolExecution = "tool_execution",
  LlmPrompt = "llm_prompt"
}

export enum AuditDecisionCode {
  IncidentTypeMissing = "incident_type_missing",
  Matched = "matched",
  NoMatch = "no_match",
  Listed = "listed",
  Registered = "registered",
  Ready = "ready",
  NotReady = "not_ready",
  GithubSynced = "github_synced",
  GithubSyncFailed = "github_sync_failed",
  LlmProviderFailed = "llm_provider_failed",
  Blocked = SharedWorkflowStateValue.Blocked,
  Redacted = "redacted",
  Clean = "clean"
}

export type AuditDecision =
  | AuditDecisionCode
  | IncidentSignalSource
  | IncidentStatus
  | IncidentNotificationState
  | IncidentDecisionState
  | IncidentOwnerEscalationState
  | CredentialStatus
  | SecretAccessDecision
  | ToolExecutionDecision
  | ToolActionReasonCode
  | SecretAccessReasonCode
  | ToolRunbookReadinessFailureCode;

export interface ApprovalBinding {
  readonly approvalId: string;
  readonly tenantId: string;
  readonly actorId: string;
  readonly resourceType: AuditResourceType;
  readonly resourceId: string;
  readonly operationHash: string;
  readonly policyClass: PolicyClass;
  readonly state: ApprovalState;
  readonly expiresAt: string;
  readonly auditCorrelationId: string;
}

export interface AuditEvent {
  readonly id: string;
  readonly tenantId: string;
  readonly actorId: string;
  readonly action: AuditAction;
  readonly resourceType: AuditResourceType;
  readonly resourceId: string;
  readonly decision: AuditDecision;
  readonly policyClass: PolicyClass | null;
  readonly auditCorrelationId: string;
  readonly createdAt: string;
}

